/**
 * Scope user CSS to a given selector using the browser's native CSS parser.
 * Handles @media, @keyframes, @supports, @container, @layer — no regex parsing.
 * Zero dependencies.
 */

// Feature-detect constructable stylesheets at module load
const HAS_CONSTRUCTABLE = (() => {
  try {
    new CSSStyleSheet();
    return true;
  } catch {
    return false;
  }
})();

// Global selectors that should be replaced (not prefixed) with the scope selector
const GLOBAL_SELECTORS = new Set([':root', 'html', 'body']);

/**
 * Extract the prelude (everything before the block) from a grouping at-rule.
 * Prefers typed properties where available; falls back to cssText parsing.
 */
function getGroupingPrelude(rule) {
  if (rule.conditionText !== undefined) {
    const name =
      rule instanceof CSSMediaRule
        ? '@media'
        : rule instanceof CSSSupportsRule
          ? '@supports'
          : rule.constructor?.name === 'CSSContainerRule'
            ? '@container'
            : null;
    if (name) return `${name} ${rule.conditionText}`;
  }
  if (rule.constructor?.name === 'CSSLayerBlockRule') {
    return rule.name ? `@layer ${rule.name}` : '@layer';
  }
  const text = rule.cssText;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '{') return text.slice(0, i).trim();
  }
  return rule.cssText.split('{')[0].trim();
}

/**
 * Split a selector list on commas, respecting parentheses and brackets.
 * Naive split(',') breaks selectors like :is(.a, .b) or [attr*=","].
 * Exported for other CSSOM walkers (mobile-menu-merge) — one splitter, not two.
 */
export function splitSelectors(selectorText) {
  const result = [];
  let current = '';
  let depth = 0;
  for (const ch of selectorText) {
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    if (ch === ',' && depth === 0) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

/**
 * Parse CSS into CSSRule[]. Uses constructable stylesheets if available,
 * falls back to temporary <style> injection for older browsers.
 */
function parseCSS(css) {
  if (HAS_CONSTRUCTABLE) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    return Array.from(sheet.cssRules);
  }
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  try {
    return style.sheet?.cssRules ? Array.from(style.sheet.cssRules) : null;
  } catch {
    return null;
  } finally {
    style.remove();
  }
}

/**
 * Scope user CSS to a given selector.
 * @param {string} css - Raw user CSS (bare declarations or full rule blocks)
 * @param {string} scopeSelector - CSS selector to scope to (e.g. '[data-pb-instance="abc"]' or '#sec-1')
 * @returns {string} Scoped CSS
 */
export function scopeCSS(css, scopeSelector) {
  if (!css || !css.trim()) return '';

  // Legacy bare declarations (no braces) — wrap in scope block
  if (!css.includes('{')) {
    return `${scopeSelector} { ${css.trim()} }`;
  }

  let rules;
  try {
    rules = parseCSS(css);
  } catch (err) {
    console.warn('[css-scoper] Parse failed, wrapping as declarations:', err.message);
    return `${scopeSelector} { ${css.replace(/[{}]/g, '').trim()} }`;
  }
  if (!rules) return `${scopeSelector} { ${css.replace(/[{}]/g, '').trim()} }`;

  return rules.map((rule) => scopeRule(rule, scopeSelector)).join('\n');
}

function scopeRule(rule, scope) {
  // @keyframes — pass through unscoped
  if (rule instanceof CSSKeyframesRule) return rule.cssText;
  // @font-face — pass through unscoped
  if (rule instanceof CSSFontFaceRule) return rule.cssText;
  // @media — recurse into children
  if (rule instanceof CSSMediaRule) {
    const inner = Array.from(rule.cssRules)
      .map((r) => scopeRule(r, scope))
      .join('\n  ');
    return `@media ${rule.conditionText} {\n  ${inner}\n}`;
  }
  // @supports — recurse into children
  if (rule instanceof CSSSupportsRule) {
    const inner = Array.from(rule.cssRules)
      .map((r) => scopeRule(r, scope))
      .join('\n  ');
    return `@supports ${rule.conditionText} {\n  ${inner}\n}`;
  }
  // Generic grouping rule fallback (@container, @layer, etc.) — recurse
  if (rule.cssRules && rule.cssRules.length > 0) {
    const inner = Array.from(rule.cssRules)
      .map((r) => scopeRule(r, scope))
      .join('\n  ');
    const prelude = getGroupingPrelude(rule);
    return `${prelude} {\n  ${inner}\n}`;
  }
  // Style rules — prefix or replace selectors
  if (rule instanceof CSSStyleRule) {
    const scoped = splitSelectors(rule.selectorText)
      .map((s) => {
        const trimmed = s.trim();
        return GLOBAL_SELECTORS.has(trimmed) ? scope : `${scope} ${trimmed}`;
      })
      .join(', ');
    return `${scoped} { ${rule.style.cssText} }`;
  }
  // Anything else — pass through
  return rule.cssText;
}
